import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Activity, RefreshCw, Loader2, ShieldCheck, ShieldAlert, ArrowLeft,
  Zap, AlertTriangle, MessageSquare, Database, Clock, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 5000;

const SEV_TONE = {
  SEV1: 'bg-red-600 text-white',
  SEV2: 'bg-amber-500 text-white',
  SEV3: 'bg-slate-400 text-white',
};

export default function AdminOpsDashboard() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/ops`, auth);
      setData(r.data);
      setLastRefresh(new Date());
    } catch (e) { /* silent */ } finally { setBusy(false); }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [user, load]);

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center text-cn-text-muted">
        Admin access required.
      </div>
    );
  }

  const healthy = data?.readyz?.ok;

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-bg-dark text-cn-text" data-testid="admin-ops-page">
      {/* Top bar */}
      <div className="border-b border-cn-line bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-violet-600" />
            <h1 className="text-lg font-semibold tracking-tight">Ops Dashboard</h1>
            {data && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                healthy ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
              }`} data-testid="admin-ops-health-pill">
                {healthy ? '● Healthy' : '● Degraded'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-cn-text-muted">
                Refreshed {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button onClick={load} className="cn-btn cn-btn-ghost text-xs" data-testid="admin-ops-refresh">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
            <Link to="/admin/tenant-health" className="text-sm text-violet-700 hover:underline flex items-center gap-1.5" data-testid="ops-tenant-health-link">
              <TrendingUp className="w-4 h-4" /> Tenant Health
            </Link>
            <Link to="/admin/dashboard" className="text-sm text-cn-text-muted hover:text-cn-text flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
          </div>
        </div>
      </div>

      {!data && (
        <div className="max-w-7xl mx-auto px-6 py-12 text-center text-cn-text-muted text-sm">
          Loading…
        </div>
      )}

      {data && (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          {/* Top KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi
              icon={AlertTriangle}
              label="Errors last 5m"
              value={data.errors.last_5m}
              tone={data.errors.last_5m > 0 ? 'red' : 'emerald'}
              testId="ops-kpi-errors-5m"
            />
            <Kpi
              icon={ShieldAlert}
              label="Open incidents"
              value={data.incidents.open}
              tone={data.incidents.open > 0 ? 'amber' : 'emerald'}
              testId="ops-kpi-open-incidents"
            />
            <Kpi
              icon={MessageSquare}
              label="Open support tickets"
              value={data.support.open}
              tone={data.support.p0 > 0 ? 'red' : (data.support.open ? 'amber' : 'emerald')}
              testId="ops-kpi-support-open"
            />
            <Kpi
              icon={Zap}
              label="Cache hit ratio"
              value={`${(data.cache.hit_ratio * 100).toFixed(0)}%`}
              tone={data.cache.hit_ratio > 0.4 ? 'emerald' : 'slate'}
              testId="ops-kpi-cache-hit"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Errors panel */}
            <Section title="Errors (last hour)" icon={<AlertTriangle className="w-4 h-4 text-red-500" />} testId="ops-errors-panel">
              {data.errors.top_fingerprints.length === 0 && (
                <div className="text-sm text-cn-text-muted italic p-4 text-center">
                  No 5xx errors in the last hour.
                </div>
              )}
              {data.errors.top_fingerprints.map((f) => (
                <div key={f.fingerprint} className="border-b border-cn-line last:border-0 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-red-700 min-w-[3ch]">{f.count}</span>
                    <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                      {f.fingerprint?.slice(0, 12)}…
                    </code>
                  </div>
                  <div className="text-xs text-cn-text-muted mt-0.5 truncate">
                    <code>{f.exception_type}</code> · {f.path}
                  </div>
                </div>
              ))}
              <div className="mt-3 text-xs">
                <Link to="/admin/system-incidents" className="text-violet-700 hover:underline">
                  Open error inbox →
                </Link>
              </div>
            </Section>

            {/* Open incidents */}
            <Section title="Incidents" icon={<ShieldAlert className="w-4 h-4 text-amber-500" />} testId="ops-incidents-panel">
              {data.incidents.top.length === 0 && (
                <div className="text-sm text-cn-text-muted italic p-4 text-center">
                  No open incidents. All quiet.
                </div>
              )}
              {data.incidents.top.map((i) => (
                <div key={i.id} className="border-b border-cn-line last:border-0 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded ${SEV_TONE[i.severity] || SEV_TONE.SEV3}`}>
                      {i.severity}
                    </span>
                    <span className="text-xs uppercase text-cn-text-muted">
                      {i.status}
                    </span>
                    <span className="text-xs text-slate-600">
                      · {i.hits} hits · {i.affected_orgs} orgs · {i.affected_users} users
                    </span>
                  </div>
                  <div className="text-xs text-cn-text-muted mt-0.5 truncate">
                    {i.latest_error?.path} · {i.latest_error?.exception_type}
                  </div>
                </div>
              ))}
              <div className="mt-3 text-xs flex gap-3">
                <Link to="/admin/incidents" className="text-violet-700 hover:underline">
                  Open incident log →
                </Link>
              </div>
            </Section>

            {/* Support KPIs */}
            <Section title="Support" icon={<MessageSquare className="w-4 h-4 text-violet-600" />} testId="ops-support-panel">
              <MetricRow label="Open tickets" value={data.support.open} />
              <MetricRow label="P0 open" value={data.support.p0} tone={data.support.p0 > 0 ? 'red' : 'slate'} />
              <MetricRow label="Auto-resolved (24h)" value={data.support.auto_resolved_24h} tone="emerald" />
              <div className="mt-3 text-xs">
                <Link to="/admin/support" className="text-violet-700 hover:underline">
                  Open support inbox →
                </Link>
              </div>
            </Section>

            {/* Cache + Pool */}
            <Section title="Cache + Pool" icon={<Database className="w-4 h-4 text-blue-500" />} testId="ops-cache-panel">
              <MetricRow label="Cache size" value={`${data.cache.size} / ${data.cache.max_entries}`} />
              <MetricRow label="Hit ratio" value={`${(data.cache.hit_ratio * 100).toFixed(0)}%`} />
              <MetricRow label="Hits" value={data.cache.hits} />
              <MetricRow label="Misses" value={data.cache.misses} />
              <div className="my-3 border-t border-cn-line" />
              <MetricRow label="Mongo max pool" value={data.mongo_pool.max_pool_size} />
              <MetricRow label="Mongo min pool" value={data.mongo_pool.min_pool_size} />
              <MetricRow label="Wait-queue timeout (s)" value={data.mongo_pool.wait_queue_timeout_seconds} />
            </Section>
          </div>

          {/* Crons — full width */}
          <Section title="Cron heartbeats" icon={<Clock className="w-4 h-4 text-cn-text-muted" />} testId="ops-crons-panel">            {data.crons.length === 0 && (
              <div className="text-sm text-cn-text-muted italic p-4 text-center">
                No cron heartbeats yet — cron jobs may not have completed their first cycle.
              </div>
            )}
            {data.crons.map((c) => (
              <div key={c.cron} className="border-b border-cn-line last:border-0 py-2 flex items-center gap-4">
                <span className={`text-[0.6rem] font-bold px-2 py-0.5 rounded ${
                  c.stale ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
                }`}>
                  {c.stale ? 'STALE' : 'ALIVE'}
                </span>
                <div className="text-sm font-medium min-w-[10rem]">{c.cron}</div>
                <div className="text-xs text-cn-text-muted flex-1">
                  Last run: <code>{c.last_run_at?.slice(11, 19)}</code>
                  <span className="mx-2">·</span>
                  Age: <code>{c.age_seconds}s</code>
                  <span className="mx-2">·</span>
                  Interval: <code>{c.interval_seconds}s</code>
                  {c.last_error && (
                    <>
                      <span className="mx-2">·</span>
                      <span className="text-red-600">Error: {c.last_error.slice(0, 60)}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </Section>

          <p className="text-xs text-cn-text-muted text-center">
            Auto-refresh every {REFRESH_MS / 1000}s. Snapshot at {data.at.slice(11, 19)} UTC.
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, testId, children }) {
  return (
    <div className="cn-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted">
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
  const toneMap = {
    red: 'text-red-700', amber: 'text-amber-700',
    emerald: 'text-emerald-700', slate: 'text-slate-700',
  };
  return (
    <div className="cn-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[0.65rem] uppercase tracking-wider text-cn-text-muted">
          {label}
        </div>
        <Icon className="w-3.5 h-3.5 text-cn-text-muted" />
      </div>
      <div className={`text-2xl font-semibold ${toneMap[tone] || toneMap.slate}`}>
        {value}
      </div>
    </div>
  );
}

function MetricRow({ label, value, tone }) {
  const t = { red: 'text-red-700', emerald: 'text-emerald-700', slate: 'text-slate-800' };
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-cn-text-muted">{label}</span>
      <span className={`font-semibold ${t[tone] || t.slate}`}>{value}</span>
    </div>
  );
}
